import axios from 'axios';

import { API_BASE_URL } from '@/config/apiBaseUrl';

const API_URL = API_BASE_URL;

// Create axios instance with proper config
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export interface Operation {
  id: string;
  company_id: string;
  operation_code: string;
  operation_name: string;
  operation_type: OperationType;
  default_workstation_type?: string;
  standard_setup_time_hours: number;
  standard_run_time_per_unit_hours: number;
  standard_teardown_time_hours: number;
  per_hour_cost: number;
  skill_level_required: SkillLevel;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export enum OperationType {
  MACHINING = 'Machining',
  ASSEMBLY = 'Assembly',
  WELDING = 'Welding',
  PAINTING = 'Painting',
  QUALITY_CHECK = 'Quality Check',
  PACKING = 'Packing',
  MATERIAL_HANDLING = 'Material Handling',
  HEAT_TREATMENT = 'Heat Treatment',
  SURFACE_TREATMENT = 'Surface Treatment',
  CUSTOM = 'Custom'
}

export enum SkillLevel {
  BASIC = 'Basic',
  INTERMEDIATE = 'Intermediate',
  ADVANCED = 'Advanced',
  EXPERT = 'Expert'
}

export interface OperationRequest {
  company_id: string;
  operation_code: string;
  operation_name: string;
  operation_type: OperationType;
  default_workstation_type?: string;
  standard_setup_time_hours?: number;
  standard_run_time_per_unit_hours?: number;
  standard_teardown_time_hours?: number;
  per_hour_cost?: number;
  skill_level_required?: SkillLevel;
  description?: string;
  is_active?: boolean;
}

export class OperationService {
  async getAll(company_id: string, activeOnly: boolean = false): Promise<Operation[]> {
    const params: any = {};
    if (company_id) {
      params.company_id = company_id;
    }
    if (activeOnly) {
      params.active_only = 'true';
    }
    
    console.log('[OperationService] getAll - params:', params);
    const response = await apiClient.get('/manufacturing/operations', { params });
    console.log('[OperationService] getAll - response:', response.data);
    
    // Handle different response structures
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (response.data?.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    if (response.data?.success && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    
    console.warn('[OperationService] Unexpected response structure:', response.data);
    return [];
  }

  async getById(id: string): Promise<Operation> {
    const response = await apiClient.get(`/manufacturing/operations/${id}`);
    return response.data.data;
  }

  async getByType(company_id: string, type: OperationType): Promise<Operation[]> {
    const response = await apiClient.get(`/manufacturing/operations/type/${type}`, {
      params: { company_id }
    });
    return response.data.data;
  }

  async search(company_id: string, searchTerm: string): Promise<Operation[]> {
    const response = await apiClient.get('/manufacturing/operations', {
      params: { company_id, search: searchTerm }
    });
    return response.data.data;
  }

  async create(data: OperationRequest): Promise<Operation> {
    const response = await apiClient.post('/manufacturing/operations', data);
    return response.data.data;
  }

  async update(id: string, data: Partial<OperationRequest>): Promise<Operation> {
    const response = await apiClient.put(`/manufacturing/operations/${id}`, data);
    return response.data.data;
  }

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/manufacturing/operations/${id}`);
  }

  async activate(id: string): Promise<Operation> {
    const response = await apiClient.post(`/manufacturing/operations/${id}/activate`);
    return response.data.data;
  }

  async deactivate(id: string): Promise<Operation> {
    const response = await apiClient.post(`/manufacturing/operations/${id}/deactivate`);
    return response.data.data;
  }
}

export default new OperationService();
